<template>
  <div class="pa-3" style="border: 1px solid #cacaca">
    <p class="mb-2 font-weight-bold" style="color: var(--fundeasy-colors-neutral-500)">
      Registration Summary
    </p>
    <v-divider class="mb-2"></v-divider>
    <v-row
      no-gutters
      v-for="(ticketPrice, i) of displayTicketSummary"
      :key="i"
      :class="{ 'mb-4': i === displayTicketSummary.length - 1 }"
    >
      <v-col class="d-flex align-start flex-column mb-1">
        <p class="fs-14">{{ ticketPrice.displayName }}</p>
        <p v-if="ticketPrice?.includedTicketCount" class="fs-12 font-weight-light-bold font-italic">
          Includes {{ ticketPrice?.includedTicketCount }} seats
        </p>
      </v-col>

      <v-col v-if="showPrices && getTicketPriceValue(ticketPrice) > 0">
        <v-row
          v-if="ticketPrice.isEarlyBird || ticketPrice.isLateRegistration"
          no-gutters
          justify="end"
        >
          <p class="fs-14" style="text-decoration: line-through">
            {{ currencyFormatter(ticketPrice?.originalPrice || 0) }}
          </p>
        </v-row>

        <v-row no-gutters justify="end">
          <p
            class="fs-14"
            :style="{
              color:
                ticketPrice.isEarlyBird || ticketPrice.isLateRegistration
                  ? 'var(--fundeasy-colors-destruction-600)'
                  : 'inherit',
            }"
          >
            {{ currencyFormatter(ticketPrice?.price || 0) }}
          </p>
        </v-row>
      </v-col>
    </v-row>
    <!-- <template v-if="Object.keys(displayAddOnSummary ?? {}).length > 0">
      <p class="mb-2" style="color: var(--fundeasy-colors-neutral-500); font-weight: bold">Add-Ons</p>
      <v-divider class="mb-2" />
      <v-row :class="{ 'mb-4': true }" no-gutters v-for="(value, name) of displayAddOnSummary" :key="name">
        <v-col>
          <p class="fs-14" style="margin-bottom: 10px">{{ name }} (x{{ value?.count }})</p>
        </v-col>
        <v-col>
          <v-row v-if="value && value.total > 0" no-gutters justify="end">
            <p class="fs-14" style="margin-bottom: 10px">${{ value ? value?.total?.toFixed(2) : "" }}</p>
          </v-row>
        </v-col>
      </v-row>
    </template> -->

    <div v-if="showPrices && baseTotalDue > 0">
      <p class="mb-2 font-weight-bold" style="color: var(--fundeasy-colors-neutral-500)">Summary</p>
      <v-divider class="mb-2"></v-divider>
      <v-row no-gutters>
        <v-col>
          <p class="fs-14">Subtotal</p>
        </v-col>

        <v-col>
          <v-row no-gutters justify="end">
            <p>{{ currencyFormatter(baseTotalDue) }}</p>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="appliedPromoCode && promoDiscountAmount > 0" no-gutters>
        <v-col>
          <p class="fs-14">{{ appliedPromoCode.name }}</p>
        </v-col>

        <v-col>
          <v-row no-gutters justify="end">
            <p class="fs-14" style="color: var(--fundeasy-colors-destruction-600)">
              -{{ currencyFormatter(promoDiscountAmount) }}
            </p>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <p class="fs-14 font-weight-light-bold">Total due</p>
        </v-col>

        <v-col>
          <v-row no-gutters justify="end">
            <p class="fs-14 font-weight-light-bold">{{ currencyFormatter(totalDue) }}</p>
          </v-row>
        </v-col>
      </v-row>
    </div>
  </div>

  <v-divider class="my-8"></v-divider>

  <div v-if="showPromoCodeSection" class="pa-3 mb-6 promo-code-card">
    <p class="mb-2 fs-14" style="color: var(--fundeasy-colors-neutral-500)">Promo Code</p>
    <v-divider class="mb-3"></v-divider>

    <v-chip
      v-if="appliedPromoCode"
      class="mb-3 promo-code-chip"
      size="small"
      closable
      @click:close="removePromoCode"
    >
      {{ appliedPromoCode.name }}
    </v-chip>

    <div class="d-flex align-start ga-2">
      <v-text-field
        v-model="promoCodeInput"
        density="compact"
        variant="outlined"
        placeholder="Enter Code"
        hide-details
        class="promo-code-input"
        @keyup.enter="applyPromoCode"
      />
      <v-btn
        variant="text"
        class="text-transform-none px-0"
        height="40"
        @click.stop="applyPromoCode"
        >Apply</v-btn
      >
    </div>
    <p v-if="promoCodeError" class="promo-code-error">{{ promoCodeError }}</p>
  </div>

  <!-- NOTE: Card-only events get no panel; with one way to pay there is nothing to choose, and the
       Continue to Payment button already says what happens next. -->
  <v-expansion-panels
    class="mb-8"
    v-model="openPanels"
    v-if="showPrices && totalDue > 0 && offlinePaymentEnabled"
  >
    <v-expansion-panel>
      <v-expansion-panel-title class="fs-14 font-weight-light-bold">
        Payment Details
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <PaymentMethodOptions v-model="paymentMode" :billing-instructions="billingInstructions" />
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>

  <div>
    <v-btn
      :color="eventData?.event_color || 'info'"
      variant="elevated"
      class="text-transform-none fs-14 font-weight-light-bold w-100"
      style="height: 48px"
      :disabled="disableContinue"
      @click.stop="
        onContinue({
          bill_me_later: paymentMode === 'offline',
          total_amount: totalDue,
          discount_code: appliedPromoCode?.name || null,
          discount_code_id: appliedPromoCode?.id || null,
        })
      "
    >
      {{ submitButtonText }}
    </v-btn>
  </div>
</template>

<script setup>
import { Currency } from '~/utils/currency';

const props = defineProps({
  displayTicketSummary: Array,
  displayAddOnSummary: Object,

  eventData: Object,
  registrations: Array,

  disableContinue: Boolean,
  onContinue: Function,
});

const paymentMode = ref('card'); // card | offline
const openPanels = ref([0]);

const offlinePaymentEnabled = computed(() => {
  return props.eventData?.is_paid_event && props.eventData?.enable_offline_payment;
});

const billingInstructions = computed(() => {
  return props.eventData?.billing_instructions;
});

const submitButtonText = computed(() => {
  if (!props.eventData?.is_paid_event || totalDue.value === 0) {
    return 'Submit Registration';
  } else {
    return paymentMode.value === 'offline' ? 'Submit Registration' : 'Continue to Payment';
  }
});

const showPrices = computed(() => {
  return props.eventData?.is_paid_event;
});

const currencyFormatter = (priceInCents) => {
  return Currency.formatCents(priceInCents);
};

function getTicketPriceValue(ticketPrice) {
  return calculateTicketPrice(ticketPrice.id, ticketPrice.isDonation, ticketPrice.price);
}

function calculateTicketPrice(id, isDonation, setPrice) {
  let totalPrice = Number(setPrice) || 0;
  if (!isDonation) {
    return totalPrice;
  }
  props.registrations?.forEach((reg) => {
    if (reg?.ticket_id === id) {
      totalPrice += reg?.ticket_price;
    }
  });
  return totalPrice;
}

const subtotal = computed(() => {
  const ticketTotal =
    props.displayTicketSummary?.reduce((acc, curr) => {
      acc += curr.price;
      return acc;
    }, 0) || 0;

  const addOnTotal =
    Object.keys(props.displayAddOnSummary ?? {}).reduce((acc, curr) => {
      acc += props.displayAddOnSummary?.[curr]?.total;
      return acc;
    }, 0) || 0;

  return Number(ticketTotal + addOnTotal);
});

const orderSubTotal = computed(() => {
  let basePrice = subtotal.value;
  props.displayTicketSummary?.forEach((ticketSummary) => {
    if (ticketSummary.isDonation) {
      props.registrations
        ?.filter((reg) => reg?.ticket_id === ticketSummary.id)
        ?.forEach((reg) => {
          basePrice += reg.ticket_price;
        });
    }
  });
  return Number(basePrice) || 0;
});

const baseTotalDue = computed(() => {
  return Math.max(0, orderSubTotal.value || 0) || Math.max(0, subtotal.value || 0);
});

const totalDue = computed(() => {
  const dueAmount = Math.max(0, baseTotalDue.value - promoDiscountAmount.value);
  return dueAmount || 0;
});

const ptaTemplateTypes = ['GENERAL_PTA', 'PTA_GALA', 'PTA_FUNDRAISER_SPONSOR'];
const promoCodeInput = ref('');
const promoCodeError = ref('');
const appliedPromoCode = ref(null);

const publicPromoCodes = computed(() => props.eventData?.public_promo_codes || []);

const showPromoCodeSection = computed(() => {
  return (
    ptaTemplateTypes.includes(props.eventData?.event_setup_template_type) &&
    publicPromoCodes.value.length > 0
  );
});

const selectedTicketQuantity = computed(() => {
  return (props.displayTicketSummary || []).reduce(
    (total, ticket) => total + (Number(ticket.qty) || 0),
    0,
  );
});

const promoEligibleSubtotal = computed(() =>
  (props.displayTicketSummary || []).reduce((total, item) => total + (Number(item.price) || 0), 0),
);

const promoDiscountAmount = computed(() => {
  if (!appliedPromoCode.value) return 0;
  const eligibleSubtotal = promoEligibleSubtotal.value;
  if (eligibleSubtotal <= 0) return 0;

  if (appliedPromoCode.value.discount_type === 'percentage') {
    return Math.min(
      eligibleSubtotal,
      Math.round(eligibleSubtotal * (Number(appliedPromoCode.value.discount_value || 0) / 100)),
    );
  }

  return Math.min(eligibleSubtotal, Number(appliedPromoCode.value.discount_value || 0) * 100);
});

function promoCodeIsExpired(code) {
  if (!code?.end_date) return false;
  return new Date() > new Date(code.end_date);
}

function promoCodeIsAvailable(code) {
  if (promoCodeIsExpired(code)) return false;
  if (code?.start_date && new Date() < new Date(code.start_date)) return false;
  if (code?.usage_limit && code?.limit > 0 && Number(code.times_used || 0) >= Number(code.limit))
    return false;
  if (code?.min_order && selectedTicketQuantity.value < Number(code.min_order)) return false;
  if (code?.max_order && selectedTicketQuantity.value > Number(code.max_order)) return false;
  return promoEligibleSubtotal.value > 0;
}

function applyPromoCode() {
  const normalizedCode = promoCodeInput.value.trim();
  promoCodeError.value = '';

  if (!normalizedCode) return;
  if (appliedPromoCode.value?.name?.toLowerCase() === normalizedCode.toLowerCase()) {
    promoCodeError.value = 'This code has already been applied to your cart.';
    return;
  }

  const promoCode = publicPromoCodes.value.find(
    (code) => code.name?.toLowerCase() === normalizedCode.toLowerCase(),
  );
  if (!promoCode) {
    promoCodeError.value =
      'You entered an invalid Promo Code. Please check the code and try again.';
    return;
  }
  if (promoCodeIsExpired(promoCode)) {
    promoCodeError.value = 'You entered a code that has expired.';
    return;
  }
  if (!promoCodeIsAvailable(promoCode)) {
    promoCodeError.value =
      'You entered an invalid Promo Code. Please check the code and try again.';
    return;
  }

  appliedPromoCode.value = promoCode;
  promoCodeInput.value = '';
}

function removePromoCode() {
  appliedPromoCode.value = null;
  promoCodeError.value = '';
}
</script>

<style scoped>
.promo-code-card {
  border: 1px solid #cacaca;
}

.promo-code-chip {
  background-color: #e0f2fe;
  color: #0e6cb3;
  font-weight: 700;
}

.promo-code-input {
  min-width: 0;
}

.promo-code-error {
  color: var(--fundeasy-colors-destruction-600);
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
}
</style>
